import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment/moment';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import classes from '../dashboard.module.scss';
import { RangeDatePicker } from '../../../_common/Form/DatePickers/RangeDatePicker/RangeDatePicker';
import Select from '../../../_common/Form/Selects/Select/Select';
import Button from '../../../_common/Form/Buttons/Button';
import FilterButton from '../../../_common/FilterButton/FilterButton';
import { getSelectRepListRequest } from '../../../../store/actions/users';
import { useQuery } from '../../../../utils/hooks/useQuery';
import utils from '../../../../utils/utils';
import Api from '../../../../api/api';
import Loader from '../../../_common/Loader/Loader';
import TableAndPagination from '../../../_common/TableAndPagination/TableAndPagination';
import accountHelpers from '../../../../utils/accountHelpers';
import { buildAdherenceTableRows } from './Charts/PatientAdherenceTable';

const TABLE_HEADER = [
  { name: 'Patient', path: 'patient' },
  { name: 'Drug', path: 'drug' },
  { name: 'PDC', path: 'pdcPct' },
  { name: 'MPR', path: 'mprPct' },
  { name: 'Gap days', path: 'gapDays' },
  { name: 'Status', path: 'statusBadge' },
];

const collectRequestData = (filters) => {
  const data = utils.deleteEmptyKeys(filters);

  if (data.start) {
    if (!data.end) {
      data.end = moment(data.start).add(1, 'day').format('YYYY-MM-DD');
    } else {
      data.end = moment(data.end).format('YYYY-MM-DD');
    }
  }

  return data;
};

const repLimit = 20;

const pdcModeData = [
  {
    title: 'Apply 292 rule',
    key: 'pdcMode',
    value: 'rule_292',
  },
];

const thisMonthStartDay = moment().startOf('month').format('YYYY-MM-DD');
const thisMonthEndDate = moment().format('YYYY-MM-DD');

const DashboardPatientAdherence = () => {
  const navigate = useNavigate();
  const { query, setQuery } = useQuery();

  const isAdmin = accountHelpers.isInRole('Admin');

  const dispatch = useDispatch();
  const { selectRepList, pageCount: repPageCount, currentPage: repCurrentPage } = useSelector((store) => store.users);

  const firstLoad = useRef(true);

  const [filters, setFilters] = useState({
    start: query.start || thisMonthStartDay,
    end: query.start && !query.end ? '' : query.end || thisMonthEndDate,
    rep: query.rep || '',
    healthPlan: query.healthPlan || '',
    pdcMode: query.pdcMode || '',
    page: query.page || '1',
    limit: query.limit || '15',
  });

  const [loading, loadingToggle] = useState(true);
  const [tableData, setTableData] = useState([]);
  const [pageCount, setPageCount] = useState(1);

  useEffect(() => {
    (async () => {
      try {
        if (firstLoad.current && isAdmin) {
          dispatch(getSelectRepListRequest({ limit: repLimit, role: 'Rep' }));
        }

        loadingToggle(true);
        const requestData = collectRequestData(filters);
        setQuery(utils.deleteEmptyKeys(filters));

        const { data } = await Api.getPatientAdherence(requestData);

        setTableData((data.data ?? []).map((d) => ({ ...d, id: d.patientId })));
        setPageCount(data._meta?.pageCount ?? 1);

        firstLoad.current = false;
        loadingToggle(false);
      } catch (e) {
        console.warn(e);
      }
    })();
  }, [filters]);

  const onChangeDate = (currentDate) => {
    setFilters((prev) => ({
      ...prev, start: currentDate[0], end: currentDate[1], page: 1,
    }));
  };

  const nextOptionListRequest = async (page) => {
    await dispatch(getSelectRepListRequest({ page, limit: repLimit, role: 'Rep' }));
  };

  const changeFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      start: thisMonthStartDay,
      end: thisMonthEndDate,
      healthPlan: query.healthPlan || '',
      rep: '',
      pdcMode: '',
      page: '1',
      limit: '15',
    });
  };

  const onChangePage = (type, count) => {
    setFilters((prev) => ({ ...prev, [type]: count }));
  };

  return (
    <div className={classes.wrapper}>
      <div className={classes.dashboard_header}>
        <div className={classes.header_wrapper}>
          <div className={classes.calendar}>
            <RangeDatePicker
              currentDate={filters}
              onChangeDate={onChangeDate}
              showLastYear
            />
          </div>

          {isAdmin && (
            <div className={classes.dashboard_select_rep}>
              <Select
                label="All reps"
                nextOptionListRequest={nextOptionListRequest}
                options={selectRepList}
                totalPage={repPageCount}
                currentPage={repCurrentPage}
                limit={repLimit}
                currentOption={filters.rep}
                onChange={(rep) => changeFilter('rep', rep)}
                optionPathname="name"
              />
            </div>
          )}

          <div style={{ width: 140, marginRight: 10 }}>
            <FilterButton
              activeValue={filters.pdcMode}
              buttonsData={pdcModeData}
              onClick={(key, value) => changeFilter(key, filters.pdcMode === value ? '' : value)}
            />
          </div>

          <div style={{ width: 130 }}>
            <Button
              title="Clear"
              clearButton
              onClick={clearFilters}
            />
          </div>
        </div>
      </div>

      <div style={{ marginTop: 20 }}>
        {firstLoad.current && loading
          ? <Loader className={classes.chart_kpi_wrapper_loader} />

          : (
            <TableAndPagination
              data={buildAdherenceTableRows(tableData)}
              header={TABLE_HEADER}
              className={classes.clickable_row}
              onClick={(patientId) => navigate(`/patients/${patientId}`)}
              onChange={onChangePage}
              totalPage={pageCount}
              loading={loading}
              page={+filters.page - 1}
              limit={+filters.limit}
            />
          )}
      </div>
    </div>
  );
};

export default DashboardPatientAdherence;
